import React, { useState, useRef, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { navStore } from '../nav/Nav'
import { navV2Store } from '../nav-v2/store'
import { useLocation } from 'react-router-dom'
import { isNavV2Enabled } from '../../util/nav-version'
import {
    METRICS,
    MetricChip,
    MetricPickerMenu,
    OperatorPill,
    ValueChip,
    ModeSelect,
} from './RuleEditorPrimitives'
import type { Op, MetricKey, MetricWithIcon } from './RuleEditorPrimitives'
import { ScrollList } from './prototypes/SingleCardPrototype'
import {
    MatchBadge,
    MT_LABEL,
    eligibleTargetKeys,
    eligibleNegationKeys,
} from './prototypes/sharedRoutingHelpers'
import type { BadgeType, MatchKey, DotStatus } from './prototypes/sharedRoutingHelpers'
import './prototypes/routing-cards.css'
import { Popover } from '../ui/popover'

// ── Types ─────────────────────────────────────────────────────────────────────

export type BidMode = 'none' | 'amount' | 'percent' | 'set'

export interface ConditionRow {
    id: number
    metric: MetricKey
    op: Op
    value: string
}

/** Normalised view model for a source ad group row. */
export interface SourceVM {
    key: string
    adGroupName: string
    campaignName: string
    status: DotStatus
    badge: BadgeType
    onRemove: () => void
}

/** Normalised view model for a destination ad group row. */
export interface DestVM {
    key: string
    adGroupName: string
    campaignName: string
    status: DotStatus
    badge: BadgeType
    /** Lowercase match-type string: 'auto' | 'product' | 'keyword' */
    matchTypeStr: string
    isOn: (k: MatchKey) => boolean
    onToggleMatch: (k: MatchKey) => void
    onRemove: () => void
}

// ── Shared primitives ─────────────────────────────────────────────────────────

/** Numbered step header used by each panel, with an optional action slot on the right. */
export function StepCard({
    step,
    title,
    subtitle,
    action,
    children,
}: {
    step: number
    title: string
    subtitle: string
    action?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <div className="relative py-5">
            <div className="relative pb-3 flex items-center gap-2">
                <span className="absolute right-full mr-4 text-[18px] font-semibold text-[oklch(0.62_0.13_250)] opacity-70 tabular-nums">
                    {step}
                </span>
                <span className="text-[20px] font-[650] tracking-[-0.02em] text-[#0E2947]">
                    {title}
                </span>
                <span className="text-[#DBE2E8] select-none">·</span>
                <span className="text-[13px] font-[450] tracking-[-0.01em] text-[#94A3B8]">
                    {subtitle}
                </span>
                {action && <div className="ml-auto">{action}</div>}
            </div>
            {children}
        </div>
    )
}

export function AddRowButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1 px-1 py-0.5 text-[13px] text-[#7289A3] hover:text-[#0E2947] rounded-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3391FF]/40"
        >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" strokeWidth="1.4">
                <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeLinecap="round" />
            </svg>
            {label}
        </button>
    )
}

/** Compact add button for StepCard header action slots. */
export function AddHeaderButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center gap-1 px-2 h-6 text-[13px] font-[500] text-[#7289A3] hover:text-[#0E2947] rounded-md hover:bg-[#F1F3F6] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3391FF]/40"
        >
            <svg width="8" height="8" viewBox="0 0 12 12" fill="none" strokeWidth="1.8">
                <path d="M6 2.5v7M2.5 6h7" stroke="currentColor" strokeLinecap="round" />
            </svg>
            {label}
        </button>
    )
}

/** Inline underlined text CTA for use inside empty-state descriptions. */
function InlineCTA({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="underline decoration-[#B8C9DB] underline-offset-2 hover:text-[#0E2947] hover:decoration-[#7289A3] transition-colors cursor-pointer"
        >
            {label}
        </button>
    )
}

export function RemoveButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="size-6 inline-flex items-center justify-center rounded-md text-[#CDD6DF] hover:text-[#FF5C5C] hover:bg-[#FFF0F0] transition shrink-0 opacity-0 group-hover:opacity-100"
            aria-label="Remove"
        >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path
                    d="M3 3l6 6M9 3l-6 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            </svg>
        </button>
    )
}

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-[#0E2947] shrink-0">
        <path
            d="M2.5 6.5l2.5 2.5 4.5-5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const LOOKBACK_PRESETS_DEFAULT = ['7', '14', '30', '60', '90', '180', '365']

export function LookbackDropdown({
    current,
    presets = LOOKBACK_PRESETS_DEFAULT,
    onPick,
    onClose,
}: {
    current: string
    presets?: string[]
    onPick: (v: string) => void
    onClose: () => void
}) {
    const ref = useRef<HTMLDivElement>(null)
    const [custom, setCustom] = useState('')

    useEffect(() => {
        const down = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose()
        }
        const key = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', down)
        document.addEventListener('keydown', key)
        return () => {
            document.removeEventListener('mousedown', down)
            document.removeEventListener('keydown', key)
        }
    }, [onClose])

    return (
        <div
            ref={ref}
            className="absolute left-0 top-full mt-1 z-30 bg-white rounded-lg ring-1 ring-[#DBE2E8] shadow-[0_8px_24px_rgba(0,0,0,0.08)] w-44 py-1 overflow-hidden"
        >
            <button
                type="button"
                onClick={() => onPick('')}
                className="w-full text-left px-3 py-1.5 hover:bg-[#F1F3F6] flex items-center justify-between"
            >
                <span className="text-[14px] text-[#0E2947] font-medium">Lifetime</span>
                {current === '' && <CheckIcon />}
            </button>
            <div className="mx-3 my-1 border-t border-[#EAEDF1]" />
            {presets.map((p) => (
                <button
                    key={p}
                    type="button"
                    onClick={() => onPick(p)}
                    className="w-full text-left px-3 py-1.5 hover:bg-[#F1F3F6] flex items-center justify-between"
                >
                    <span className="text-[14px] text-[#0E2947] font-medium tabular-nums">
                        {p} days
                    </span>
                    {p === current && <CheckIcon />}
                </button>
            ))}
            <div className="mx-3 my-1 border-t border-[#EAEDF1]" />
            <div className="px-3 py-2">
                <div className="text-[12px] text-[#899CB3] mb-1.5">Custom</div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        const n = parseInt(custom, 10)
                        if (!isNaN(n) && n > 0) {
                            onPick(String(n))
                            setCustom('')
                        }
                    }}
                    className="flex items-center gap-1.5"
                >
                    <input
                        type="text"
                        inputMode="numeric"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="e.g. 45"
                        className="w-full h-7 px-2 rounded-md bg-[#FAFBFC] ring-1 ring-[#DBE2E8] text-[14px] text-[#0E2947] outline-none focus:ring-[#0277A8]/50 focus:ring-2 tabular-nums placeholder:text-[#C8D2DC]"
                    />
                    <button
                        type="submit"
                        className="h-7 px-2 rounded-md bg-[#0277A8] text-white text-[13px] font-medium shrink-0 hover:bg-[#016690] transition disabled:opacity-40"
                        disabled={!custom || isNaN(parseInt(custom, 10))}
                    >
                        Set
                    </button>
                </form>
            </div>
        </div>
    )
}

// ── Generic row display components ────────────────────────────────────────────

const XIcon = () => (
    <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
        <path
            d="M2 2 L8 8 M8 2 L2 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
)

function NamePopover({
    children,
    className,
    targetTagName = 'span',
}: {
    children: string
    className: string
    targetTagName?: keyof JSX.IntrinsicElements
}) {
    return (
        <Popover
            content={<div className="pm-route-name-popover-content">{children}</div>}
            interactionKind="hover"
            position="top"
            minimal
            hoverOpenDelay={250}
            className={className}
            targetTagName={targetTagName}
        >
            {children}
        </Popover>
    )
}

export function SourceRowDisplay({ vm }: { vm: SourceVM }) {
    return (
        <div className="pm-route-srcrow group/card">
            <div className="pm-route-srcrow-main">
                <div className="pm-route-srcname">
                    <span className={`status-dot status-dot--${vm.status}`} />
                    <NamePopover className="pm-route-destname-text">{vm.adGroupName}</NamePopover>
                    <MatchBadge type={vm.badge} />
                </div>
                <NamePopover className="pm-route-srccamp" targetTagName="div">
                    {vm.campaignName}
                </NamePopover>
            </div>
            <button
                type="button"
                className="pm-chip-x pm-route-src-x opacity-0 group-hover/card:opacity-100 transition-opacity"
                onClick={vm.onRemove}
                aria-label="Remove source"
            >
                <XIcon />
            </button>
        </div>
    )
}

export function DestRowDisplay({ vm, lane }: { vm: DestVM; lane: 'promote' | 'negate' }) {
    const keys =
        lane === 'promote'
            ? eligibleTargetKeys(vm.matchTypeStr)
            : eligibleNegationKeys(vm.matchTypeStr)
    const variant = lane === 'promote' ? 'tgt' : 'neg'
    return (
        <div className="pm-route-dest group/card">
            <div className="pm-route-destname">
                <span className={`status-dot status-dot--${vm.status}`} />
                <NamePopover className="pm-route-destname-text">{vm.adGroupName}</NamePopover>
                <MatchBadge type={vm.badge} />
                <button
                    type="button"
                    className="pm-chip-x pm-route-dest-x opacity-0 group-hover/card:opacity-100 transition-opacity"
                    onClick={vm.onRemove}
                    aria-label="Remove"
                >
                    <XIcon />
                </button>
            </div>
            <NamePopover className="pm-route-destcamp" targetTagName="div">
                {vm.campaignName}
            </NamePopover>
            <div className="pm-route-mts">
                {keys.map((k) => (
                    <button
                        key={k}
                        type="button"
                        className={
                            `pm-pill pm-pill--${variant}` + (vm.isOn(k) ? ' pm-pill--on' : '')
                        }
                        onClick={() => vm.onToggleMatch(k)}
                    >
                        {MT_LABEL[k]}
                    </button>
                ))}
            </div>
        </div>
    )
}

// ── ConditionsPanel ───────────────────────────────────────────────────────────

const SCROLL_AT = 4

export interface ConditionsPanelProps {
    isKdp?: boolean
    conditions: ConditionRow[]
    lookback: string
    recommendMaxLookbackDays?: number
    userStartDateLabel?: string
    onAddCondition: () => void
    onRemoveCondition: (id: number) => void
    onSwapMetric: (id: number, picked: MetricWithIcon) => void
    onUpdateOp: (id: number, op: Op) => void
    onUpdateValue: (id: number, value: string) => void
    onLookbackChange: (v: string) => void
    currencySymbol?: string
    action?: React.ReactNode
}

export function ConditionsPanel({
    isKdp = false,
    conditions,
    lookback,
    recommendMaxLookbackDays = Infinity,
    userStartDateLabel,
    onAddCondition,
    onRemoveCondition,
    onSwapMetric,
    onUpdateOp,
    onUpdateValue,
    onLookbackChange,
    currencySymbol = '$',
    action,
}: ConditionsPanelProps) {
    const availableMetrics = METRICS.filter((m) => isKdp || !m.kdpOnly)
    const [openMenu, setOpenMenu] = useState<number | null>(null)
    const [menuQuery, setMenuQuery] = useState('')
    const [openLookback, setOpenLookback] = useState(false)

    const pickerMetrics = (excludeId: number | null): MetricWithIcon[] =>
        availableMetrics.filter((m) => {
            const inUse = conditions.some((c) => c.metric === m.key && c.id !== excludeId)
            if (inUse) return false
            return m.label.toLowerCase().includes(menuQuery.toLowerCase())
        })

    const lookbackNum = Number(lookback)
    const showLookbackWarning =
        lookback !== '' &&
        !isNaN(lookbackNum) &&
        isFinite(recommendMaxLookbackDays) &&
        lookbackNum > recommendMaxLookbackDays

    const conditionsAction = (
        <div className="flex items-center gap-1.5">
            {action}
            <AddHeaderButton label="Add condition" onClick={onAddCondition} />
        </div>
    )

    return (
        <StepCard
            step={1}
            title="Conditions"
            subtitle="When a search term matches all of the following"
            action={conditionsAction}
        >
            <div>
                {conditions.length === 0 ? (
                    <div className="flex items-start gap-2.5 py-1.5">
                        {/* filter / condition glyph */}
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="mt-0.5 shrink-0 text-[#C8D2DC]"
                        >
                            <circle
                                cx="8"
                                cy="8"
                                r="5.5"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeDasharray="2.5 1.8"
                            />
                            <path
                                d="M5.5 6.5h5M6.5 8.5h3M7.5 10.5h1"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                            />
                        </svg>
                        <div>
                            <div className="text-[13px] font-[500] tracking-[-0.01em] text-[#7289A3]">
                                No conditions
                            </div>
                            <div className="text-[13px] font-[450] tracking-[-0.01em] text-[#8599AE] mt-0.5">
                                Rule fires on every search term.{' '}
                                <InlineCTA label="Add a condition" onClick={onAddCondition} /> to
                                filter by performance.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="flex flex-col gap-y-2 rounded-md bg-[#FAFBFC] py-2 max-w-[560px]"
                        style={{ border: '1px solid rgba(15,23,42,.06)' }}
                    >
                        {conditions.map((c) => {
                            const meta = METRICS.find((m) => m.key === c.metric)!
                            const editing = openMenu === c.id
                            return (
                                <div key={c.id} className="group flex items-center gap-2 px-2">
                                    <div className="relative inline-flex ring-1 ring-[#DBE2E8] rounded-md overflow-visible [&_button]:ring-0 [&_button]:rounded-none [&_button]:shadow-none [&>:first-child>button]:rounded-l-md [&>span]:ring-0 [&>span]:rounded-none [&>span]:shadow-none [&>span:last-child]:rounded-r-md divide-x divide-[#EDF0F3]">
                                        <div className="relative">
                                            <MetricChip
                                                icon={meta.icon}
                                                label={meta.label}
                                                active={editing}
                                                hideIcon
                                                onClick={() => {
                                                    setOpenMenu(editing ? null : c.id)
                                                    setMenuQuery('')
                                                }}
                                            />
                                            {editing && (
                                                <div className="absolute left-0 top-full mt-1 z-20">
                                                    <MetricPickerMenu
                                                        query={menuQuery}
                                                        onQueryChange={setMenuQuery}
                                                        metrics={pickerMetrics(c.id)}
                                                        currentKey={c.metric}
                                                        onPick={(picked) => {
                                                            onSwapMetric(c.id, picked)
                                                            setOpenMenu(null)
                                                            setMenuQuery('')
                                                        }}
                                                        onClose={() => setOpenMenu(null)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <OperatorPill
                                            value={c.op}
                                            onChange={(op) => onUpdateOp(c.id, op)}
                                        />
                                        <ValueChip
                                            prefix={
                                                meta.currencyPrefix ? currencySymbol : meta.prefix
                                            }
                                            suffix={meta.suffix}
                                            value={c.value}
                                            onChange={(v) => onUpdateValue(c.id, v)}
                                        />
                                    </div>
                                    <RemoveButton onClick={() => onRemoveCondition(c.id)} />
                                </div>
                            )
                        })}

                        {/* Lookback row — inside the conditions block */}
                        <div className="flex items-center gap-2 px-2 pt-1">
                            <span className="text-[13px] font-[450] tracking-[-0.01em] text-[#94A3B8]">
                                Over the last
                            </span>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setOpenLookback((o) => !o)}
                                    className={
                                        'inline-flex items-center gap-1.5 h-7 pl-2.5 pr-2 rounded-md ring-1 text-[14px] text-[#0E2947] font-medium cursor-pointer transition ' +
                                        (openLookback
                                            ? 'ring-[#0277A8]/50 ring-2 bg-[#FAFBFC]'
                                            : 'ring-[#DBE2E8] bg-[#FAFBFC] hover:bg-[#F1F3F6]')
                                    }
                                >
                                    <span className="tabular-nums">
                                        {lookback ? `${lookback} days` : 'Lifetime'}
                                    </span>
                                    <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 10 10"
                                        fill="none"
                                        className="text-[#B0BCCA]"
                                    >
                                        <path
                                            d="M2.5 4l2.5 2.5L7.5 4"
                                            stroke="currentColor"
                                            strokeWidth="1.25"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                                {openLookback && (
                                    <LookbackDropdown
                                        current={lookback}
                                        onPick={(v) => {
                                            onLookbackChange(v)
                                            setOpenLookback(false)
                                        }}
                                        onClose={() => setOpenLookback(false)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showLookbackWarning && (
                    <div
                        className="mt-2 max-w-[560px] flex gap-2.5 rounded-lg px-3 py-2.5 text-[13px] leading-relaxed"
                        style={{ background: '#FFFBEB', border: '1px solid rgba(217,119,6,.2)' }}
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="shrink-0 mt-0.5 text-amber-500"
                        >
                            <path
                                d="M8 2.5L1.5 13.5h13L8 2.5z"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M8 6.5v3"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                            <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
                        </svg>
                        <div className="text-amber-900">
                            <span className="font-[600]">
                                Lookback extends before your data start.
                            </span>{' '}
                            Merch Jar has data for your profile from{' '}
                            {userStartDateLabel ?? `~${recommendMaxLookbackDays} days ago`} onwards.
                            A {lookback}-day lookback may return fewer matches than expected — we
                            recommend{' '}
                            <span className="font-[600] tabular-nums">
                                {recommendMaxLookbackDays} days
                            </span>{' '}
                            or less.
                        </div>
                    </div>
                )}
            </div>
        </StepCard>
    )
}

// ── SourcesPanel ──────────────────────────────────────────────────────────────

export interface SourcesPanelProps {
    sources: SourceVM[]
    onAdd: () => void
    action?: React.ReactNode
}

export function SourcesPanel({ sources, onAdd, action }: SourcesPanelProps) {
    const sourcesAction = (
        <div className="flex items-center gap-1.5">
            {action}
            <AddHeaderButton label="Add source" onClick={onAdd} />
        </div>
    )

    return (
        <StepCard
            step={2}
            title="Sources"
            subtitle="Where matching search terms come from"
            action={sourcesAction}
        >
            <div>
                {sources.length === 0 ? (
                    <div className="flex items-start gap-2.5 py-1.5">
                        {/* routing glyph */}
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="mt-0.5 shrink-0 text-[#C8D2DC]"
                        >
                            <circle
                                cx="8"
                                cy="8"
                                r="5.5"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeDasharray="2.5 1.8"
                            />
                            <path
                                d="M8 5v3.5l2 2"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div>
                            <div className="text-[13px] font-[500] tracking-[-0.01em] text-[#7289A3]">
                                No sources
                            </div>
                            <div className="text-[13px] font-[450] tracking-[-0.01em] text-[#8599AE] mt-0.5">
                                <InlineCTA label="Add a source" onClick={onAdd} /> to pull search
                                terms from.
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="routing-cards-root">
                        <ScrollList
                            scrollable={sources.length > SCROLL_AT}
                            className={
                                'pm-route-srclist !mt-0' +
                                (sources.length > SCROLL_AT ? ' pm-route-srclist--scroll' : '')
                            }
                        >
                            {sources.map((vm) => (
                                <SourceRowDisplay key={vm.key} vm={vm} />
                            ))}
                        </ScrollList>
                    </div>
                )}
            </div>
        </StepCard>
    )
}

// ── RoutingPanel ──────────────────────────────────────────────────────────────

export interface RoutingPanelProps {
    promote: DestVM[]
    negate: DestVM[]
    onAddPromote: () => void
    onAddNegate: () => void
    bidMode: BidMode
    bidValue: string
    bidPrefix: string
    bidSuffix: string
    onBidModeChange: (mode: BidMode) => void
    onBidValueChange: (v: string) => void
    currencySymbol?: string
    action?: React.ReactNode
}

export function RoutingPanel({
    promote,
    negate,
    onAddPromote,
    onAddNegate,
    bidMode,
    bidValue,
    bidPrefix,
    bidSuffix,
    onBidModeChange,
    onBidValueChange,
    currencySymbol = '$',
    action,
}: RoutingPanelProps) {
    return (
        <StepCard
            step={3}
            title="Destinations"
            subtitle="Choose where matching terms should go"
            action={action}
        >
            <div className="routing-cards-root">
                <div className="pm-route-body rounded-lg overflow-hidden ring-1 ring-[#DBE2E8]">
                    {/* Promote column */}
                    <div className="pm-route-col pm-route-col--tgt">
                        <div className="pm-route-coltitle">
                            <span className="pm-bullet pm-bullet--tgt" />
                            Promote matches to
                            <span className="ml-auto">
                                <AddHeaderButton label="Add promotion" onClick={onAddPromote} />
                            </span>
                        </div>
                        {promote.length === 0 ? (
                            <div className="flex items-start gap-2.5 py-1.5">
                                {/* routing arrow glyph */}
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    className="mt-0.5 shrink-0 text-[#C8D2DC]"
                                >
                                    <path
                                        d="M3 8h10M9 4l4 4-4 4"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div>
                                    <div className="text-[13px] font-[500] tracking-[-0.01em] text-[#7289A3]">
                                        No promotion targets
                                    </div>
                                    <div className="text-[13px] font-[450] tracking-[-0.01em] text-[#8599AE] mt-0.5">
                                        Matching terms won't be promoted.{' '}
                                        <InlineCTA label="Add a promotion" onClick={onAddPromote} />{' '}
                                        to get started.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ScrollList
                                scrollable={promote.length > SCROLL_AT}
                                className={
                                    'pm-route-destlist' +
                                    (promote.length > SCROLL_AT ? ' pm-route-destlist--scroll' : '')
                                }
                            >
                                {promote.map((vm) => (
                                    <DestRowDisplay key={vm.key} vm={vm} lane="promote" />
                                ))}
                            </ScrollList>
                        )}
                        {/* Bid behavior — below promote column, subtle divider */}
                        <div
                            className="mt-2 pt-2"
                            style={{ borderTop: '1px solid rgba(15,23,42,.05)' }}
                        >
                            <div className="text-[12px] font-[500] tracking-[0.01em] uppercase text-[#C8D2DC] mb-1">
                                Bid behavior
                            </div>
                            <p className="text-[13px] font-[450] tracking-[-0.01em] text-[#7289A3] mb-2">
                                Set the starting bid for the promoted term. By default it inherits
                                the{' '}
                                <a
                                    href="https://merchjar.com/help-center/docs/smart-campaign-bid-resolution"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="underline decoration-[#CDD6DF] underline-offset-2 hover:decoration-[#0E2947] hover:text-[#0E2947] transition"
                                >
                                    resolved bid
                                </a>
                                .
                            </p>
                            <div className="inline-flex ring-1 ring-[#DBE2E8] rounded-md overflow-hidden divide-x divide-[#DBE2E8] [&_select]:ring-0 [&_select]:shadow-none [&_select]:rounded-none [&>span]:ring-0 [&>span]:rounded-none">
                                <ModeSelect
                                    value={bidMode}
                                    onChange={onBidModeChange}
                                    options={[
                                        { value: 'none', label: 'Keep resolved bid' },
                                        { value: 'amount', label: `Increase by ${currencySymbol}` },
                                        { value: 'percent', label: 'Increase by %' },
                                        { value: 'set', label: `Set bid to ${currencySymbol}` },
                                    ]}
                                />
                                {bidMode !== 'none' && (
                                    <ValueChip
                                        value={bidValue}
                                        onChange={onBidValueChange}
                                        prefix={bidPrefix}
                                        suffix={bidSuffix}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Negate column */}
                    <div className="pm-route-col pm-route-col--neg">
                        <div className="pm-route-coltitle">
                            <span className="pm-bullet pm-bullet--neg" />
                            Negate matches in
                            <span className="ml-auto">
                                <AddHeaderButton label="Add negation" onClick={onAddNegate} />
                            </span>
                        </div>
                        {negate.length === 0 ? (
                            <div className="flex items-start gap-2.5 py-1.5">
                                {/* minus-circle glyph */}
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    className="mt-0.5 shrink-0 text-[#C8D2DC]"
                                >
                                    <circle
                                        cx="8"
                                        cy="8"
                                        r="5.5"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeDasharray="2.5 1.8"
                                    />
                                    <path
                                        d="M5.5 8h5"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <div>
                                    <div className="text-[13px] font-[500] tracking-[-0.01em] text-[#7289A3]">
                                        No negation targets
                                    </div>
                                    <div className="text-[13px] font-[450] tracking-[-0.01em] text-[#8599AE] mt-0.5">
                                        Matched terms won't be excluded.{' '}
                                        <InlineCTA label="Add a negation" onClick={onAddNegate} />{' '}
                                        to block from campaigns.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <ScrollList
                                scrollable={negate.length > SCROLL_AT}
                                className={
                                    'pm-route-destlist' +
                                    (negate.length > SCROLL_AT ? ' pm-route-destlist--scroll' : '')
                                }
                            >
                                {negate.map((vm) => (
                                    <DestRowDisplay key={vm.key} vm={vm} lane="negate" />
                                ))}
                            </ScrollList>
                        )}
                    </div>
                </div>
            </div>
        </StepCard>
    )
}

// ── SaveBar ───────────────────────────────────────────────────────────────────

function Kbd({
    children,
    variant = 'dark',
}: {
    children: React.ReactNode
    variant?: 'dark' | 'light'
}) {
    return variant === 'light' ? (
        <kbd className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded text-[10px] font-medium bg-[#EAEDF1] border border-[#D1D8E0] text-[#94A3B8]">
            {children}
        </kbd>
    ) : (
        <kbd className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded text-[10px] font-medium bg-white/10 border border-white/15 text-white/70">
            {children}
        </kbd>
    )
}

export interface SaveBarProps {
    onSave: () => void
    onCancel: () => void
    isSaving?: boolean
    isNew?: boolean
    isValid?: boolean
    disabledReason?: string
    saveLabel?: string
}

export const SaveBar = observer(function SaveBar({
    onSave,
    onCancel,
    isSaving = false,
    isNew = false,
    isValid = true,
    disabledReason,
    saveLabel,
}: SaveBarProps) {
    const saveRef = useRef<HTMLButtonElement>(null)
    const isMac =
        typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
    const [mounted, setMounted] = useState(false)
    const { search } = useLocation()
    const useNavV2 = isNavV2Enabled(search)

    useEffect(() => {
        // Tick after mount so the translate-y transition fires
        const id = requestAnimationFrame(() => setMounted(true))
        return () => cancelAnimationFrame(id)
    }, [])

    // Mirror the same offset logic used in App.jsx so the bar never overlaps
    // the left nav when it is open and pinned.
    const navOffset =
        navStore.open && navStore.isPinned ? (useNavV2 ? navV2Store.widthStyle : '16em') : '0'

    return (
        <div
            className={`fixed bottom-0 right-0 h-12 border-t border-[#DBE2E8]/80 bg-white/85 backdrop-blur-md z-30 transition-transform duration-[180ms] ease-out shadow-[0_-8px_24px_rgba(14,41,71,0.09),0_-1px_4px_rgba(14,41,71,0.06)] ${mounted ? 'translate-y-0' : 'translate-y-full'}`}
            style={{ left: navOffset }}
        >
            <div className="max-w-5xl mx-auto h-full flex items-center justify-between">
                <div
                    className="flex items-center gap-2 text-[13px] text-[#7289A3]"
                    aria-live="polite"
                >
                    {!isValid ? (
                        <>
                            <span className="size-1.5 rounded-full bg-[#FF5C5C]" aria-hidden />
                            {disabledReason ?? 'Fix invalid fields before saving'}
                        </>
                    ) : (
                        <>
                            <span className="size-1.5 rounded-full bg-amber-400" aria-hidden />
                            Unsaved changes
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        className="px-2.5 h-8 text-[14px] font-[600] tracking-[-0.01em] text-[#7289A3] hover:text-[#0E2947] rounded-md hover:bg-[#EAEDF1] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3391FF]/40 disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                        Cancel
                        <Kbd variant="light">Esc</Kbd>
                    </button>
                    <button
                        ref={saveRef}
                        type="button"
                        onClick={onSave}
                        disabled={!isValid || isSaving}
                        className="px-3 h-8 text-[14px] font-[600] tracking-[-0.01em] bg-[#0277A8] hover:bg-[#016690] disabled:bg-[#0277A8]/40 disabled:cursor-not-allowed text-white rounded-md inline-flex items-center gap-2 shadow-[0_1px_2px_rgba(14,41,71,0.12),inset_0_1px_0_rgba(255,255,255,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0277A8]/40 transition"
                    >
                        {isSaving
                            ? 'Saving…'
                            : (saveLabel ?? (isNew ? 'Create Rule' : 'Save changes'))}
                        {!isSaving && (
                            <span className="flex items-center gap-0.5">
                                <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
                                <Kbd>S</Kbd>
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
})
